(() => {
  if (customElements.get('product-detail-nav')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileMq = window.matchMedia('(max-width: 767px)');

  class ProductDetailNav extends HTMLElement {
    connectedCallback() {
      this.nav = this.querySelector('[data-pdn-nav]');
      this.list = this.querySelector('[data-pdn-list]');
      this.bar = this.querySelector('.pdn-nav__bar');
      // Placeholder that keeps the bar's place in the page while the bar is fixed at the top
      this.spacer = this.querySelector('.pdn-nav-spacer');
      if (!this.spacer) {
        this.spacer = document.createElement('div');
        this.spacer.className = 'pdn-nav-spacer';
        this.spacer.setAttribute('aria-hidden', 'true');
        this.nav.before(this.spacer);
      }
      this.links = Array.from(this.querySelectorAll('[data-pdn-link]'));
      this.panels = this.links
        .map((link) => this.panelFor(link))
        .filter(Boolean);
      this.fullStick = this.classList.contains('pdn--stick-full');
      // With the full-width sticky bar there is no gap under the header / sticky add-to-cart
      this.extra = this.fullStick ? 0 : parseInt(this.dataset.extraOffset || '0', 10) || 0;
      this.headers = [];
      this.activeId = null;
      this.ticking = false;

      this.collectHeaders();

      this.onScroll = () => {
        if (!this.ticking) {
          this.ticking = true;
          requestAnimationFrame(() => {
            this.update();
            this.ticking = false;
          });
        }
        // The theme header and the sticky add-to-cart bar slide in/out with an animation.
        // Measure again once scrolling stops and those animations have finished,
        // so the bar never stays positioned under a header that has already left.
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.update(), 450);
      };
      // Any slide animation on the page (header hiding, add-to-cart bar appearing) → measure again
      this.onTransitionEnd = (event) => {
        if (event.propertyName === 'transform' || event.propertyName === 'visibility') this.onScroll();
      };
      document.addEventListener('transitionend', this.onTransitionEnd, true);
      this.onResize = () => {
        this.collectHeaders();
        this.fixStickyParents();
        this.update();
        this.updateListEdges();
      };
      document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
      window.addEventListener('resize', this.onResize);

      // Sticky breaks if a parent has overflow: hidden (common on mobile) – switch it to clip
      this.fixStickyParents();

      this.links.forEach((link) => {
        link.addEventListener('click', (event) => {
          const target = this.panelFor(link);
          if (!target) return;
          event.preventDefault();
          this.scrollToPanel(target);
          history.replaceState(null, '', '#' + target.id);
          this.setActive(target.id);
        });
      });

      // Any other link on the page that points to one of the panels (e.g. the product rating
      // stars linking to #recenzii) scrolls the same way as the bar links and marks the tab active
      this.onDocumentClick = (event) => {
        const link = event.target.closest && event.target.closest('a[href*="#"], [data-pdn-target]');
        if (!link || this.contains(link)) return;
        let id = link.dataset.pdnTarget;
        if (!id) {
          const href = link.getAttribute('href') || '';
          const hashIndex = href.indexOf('#');
          const path = href.slice(0, hashIndex);
          if (path && path !== window.location.pathname) return; // link to another page
          id = decodeURIComponent(href.slice(hashIndex + 1));
        }
        const target = id && this.panels.find((panel) => panel.id === id);
        if (!target) return;
        event.preventDefault();
        this.scrollToPanel(target);
        history.replaceState(null, '', '#' + target.id);
        this.setActive(target.id);
      };
      document.addEventListener('click', this.onDocumentClick);

      // Fade the edge of the link list that has more links hidden
      this.onListScroll = () => this.updateListEdges();
      this.list.addEventListener('scroll', this.onListScroll, { passive: true });

      this.initDescriptions();
      this.update();
      this.updateListEdges();

      // Page opened with #anchor in the URL
      const hash = decodeURIComponent(window.location.hash.slice(1));
      const hashTarget = hash && this.panels.find((panel) => panel.id === hash);
      if (hashTarget) {
        const go = () => this.scrollToPanel(hashTarget, 'auto');
        document.readyState === 'complete' ? setTimeout(go, 50) : window.addEventListener('load', go, { once: true });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('scroll', this.onScroll, { capture: true });
      document.removeEventListener('click', this.onDocumentClick);
      clearTimeout(this.correctTimer);
      if (this.onScrollEnd) window.removeEventListener('scrollend', this.onScrollEnd);
      document.removeEventListener('transitionend', this.onTransitionEnd, true);
      clearTimeout(this.settleTimer);
      window.removeEventListener('resize', this.onResize);
      this.setCovered([]);
    }

    panelFor(link) {
      const id = decodeURIComponent((link.getAttribute('href') || '').slice(1));
      return id ? document.getElementById(id) : null;
    }

    collectHeaders() {
      const selector = this.dataset.headerSelector;
      this.headers = [];
      if (!selector) return;
      try {
        document.querySelectorAll(selector).forEach((el) => {
          if (!this.contains(el) && !el.contains(this)) this.headers.push(el);
        });
      } catch (e) { /* invalid selector */ }
    }

    /* Bottom edge of everything fixed / sticky at the top of the screen
       (theme header, sticky add-to-cart bar, announcement bar...) */
    headerOffset() {
      let bottom = 0;
      this.headers.forEach((el) => {
        if (!ProductDetailNav.isTopBar(el)) return;
        const rect = el.getBoundingClientRect();
        if (rect.top <= 1 && rect.bottom > bottom) bottom = rect.bottom;
      });
      return Math.max(0, Math.round(this.probeTop(bottom)));
    }

    /* A real bar at the top of the page: fixed/sticky, visible and at least half the screen wide.
       Skips hidden elements such as the cart drawer header, which is sticky but invisible. */
    static isTopBar(el) {
      const cs = getComputedStyle(el);
      if (cs.position !== 'sticky' && cs.position !== 'fixed') return false;
      if (cs.visibility === 'hidden' || cs.display === 'none' || parseFloat(cs.opacity) === 0) return false;
      const rect = el.getBoundingClientRect();
      return rect.height > 0 && rect.width >= document.documentElement.clientWidth * 0.5;
    }

    /* Looks at what sits on top of the page just below `start` and keeps going down
       while it finds fixed / sticky bars stacked under each other. */
    probeTop(start) {
      const vw = document.documentElement.clientWidth;
      const vh = window.innerHeight;
      const xs = [vw * 0.5, vw * 0.2, vw * 0.8];
      const found = [];
      let y = Math.max(0, start);
      for (let i = 0; i < 4; i += 1) {
        let next = y;
        xs.forEach((x) => {
          const hits = document.elementsFromPoint(x, Math.min(vh - 1, y + 1));
          const hit = hits.find((el) => !this.contains(el));
          const bar = hit ? this.fixedAncestor(hit) : null;
          if (!bar) return;
          const rect = bar.getBoundingClientRect();
          if (rect.top <= y + 1 && rect.bottom > next && rect.height < vh * 0.4) {
            next = rect.bottom;
            if (!found.includes(bar)) found.push(bar);
          }
        });
        if (next <= y) break;
        y = next;
      }
      this.coverEls = found;
      return y;
    }

    fixedAncestor(el) {
      for (let n = el; n && n !== document.body && n !== document.documentElement; n = n.parentElement) {
        if (this.contains(n) || n.contains(this)) return null;
        const pos = getComputedStyle(n).position;
        if (pos === 'fixed' || pos === 'sticky') return ProductDetailNav.isTopBar(n) ? n : null;
      }
      return null;
    }

    /* While the bar is stuck, the bars right above it drop their shadow,
       so it doesn't fall over the navigation. */
    setCovered(els) {
      (this.covered || []).forEach((el) => {
        if (!els.includes(el)) el.classList.remove('pdn-flat-shadow');
      });
      els.forEach((el) => el.classList.add('pdn-flat-shadow'));
      this.covered = els;
    }

    fixStickyParents() {
      if (!window.CSS || !CSS.supports('overflow', 'clip')) return;
      for (let n = this.parentElement; n && n !== document.body && n !== document.documentElement; n = n.parentElement) {
        const cs = getComputedStyle(n);
        if (cs.overflowX !== 'hidden' && cs.overflowY !== 'hidden') continue;
        n.style.setProperty('overflow-x', cs.overflowX === 'hidden' ? 'clip' : 'visible');
        n.style.setProperty('overflow-y', cs.overflowY === 'hidden' ? 'clip' : 'visible');
      }
    }

    update() {
      const top = this.headerOffset() + this.extra;
      if (top !== this.lastTop) {
        this.style.setProperty('--pdn-top', top + 'px');
        this.lastTop = top;
      }
      const navH = this.nav.offsetHeight;
      if (navH !== this.lastNavH) {
        this.style.setProperty('--pdn-nav-h', navH + 'px');
        this.lastNavH = navH;
      }

      // Once the bar reaches the top it becomes fixed, so it stays visible to the end of the page
      const stuck = this.getBoundingClientRect().top < top - 1;
      this.nav.classList.toggle('is-stuck', stuck);
      this.nav.classList.toggle('is-fixed', stuck);
      this.spacer.classList.toggle('is-active', stuck);
      if (stuck) {
        const slot = this.spacer.getBoundingClientRect();
        const fixLeft = Math.round(slot.left);
        const fixW = Math.round(slot.width);
        if (fixLeft !== this.lastFixLeft || fixW !== this.lastFixW) {
          this.style.setProperty('--pdn-fix-left', fixLeft + 'px');
          this.style.setProperty('--pdn-fix-w', fixW + 'px');
          this.lastFixLeft = fixLeft;
          this.lastFixW = fixW;
        }
      }
      this.setCovered(stuck ? this.coverEls || [] : []);

      // Distance from the bar to the viewport edges, used to stretch it to full width when stuck
      if (this.fullStick) {
        // The bar's normal position is the content box of its parent container
        const host = this.bar.parentElement;
        const rect = host.getBoundingClientRect();
        const cs = getComputedStyle(host);
        const vw = document.documentElement.clientWidth;
        const bleedL = Math.max(0, Math.round(rect.left + parseFloat(cs.paddingLeft) + parseFloat(cs.borderLeftWidth)));
        const bleedR = Math.max(0, Math.round(vw - (rect.right - parseFloat(cs.paddingRight) - parseFloat(cs.borderRightWidth))));
        if (bleedL !== this.lastBleedL || bleedR !== this.lastBleedR) {
          this.style.setProperty('--pdn-bleed-l', bleedL + 'px');
          this.style.setProperty('--pdn-bleed-r', bleedR + 'px');
          this.lastBleedL = bleedL;
          this.lastBleedR = bleedR;
        }
      }

      if (!this.panels.length) return;
      const line = top + navH + 32;
      let current = this.panels[0];
      this.panels.forEach((panel) => {
        if (panel.getBoundingClientRect().top - line <= 0) current = panel;
      });

      // At the bottom of the page, mark the last panel as active if visible
      const last = this.panels[this.panels.length - 1];
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
      if (atBottom && last.getBoundingClientRect().top < window.innerHeight * 0.7) current = last;

      this.setActive(current.id);
    }

    updateListEdges() {
      const max = this.list.scrollWidth - this.list.clientWidth;
      this.list.classList.toggle('can-scroll-left', max > 2 && this.list.scrollLeft > 2);
      this.list.classList.toggle('can-scroll-right', max > 2 && this.list.scrollLeft < max - 2);
    }

    setActive(id) {
      if (id === this.activeId) return;
      this.activeId = id;
      let activeLink = null;
      this.links.forEach((link) => {
        const isActive = decodeURIComponent(link.getAttribute('href').slice(1)) === id;
        link.classList.toggle('is-active', isActive);
        if (isActive) {
          link.setAttribute('aria-current', 'true');
          activeLink = link;
        } else {
          link.removeAttribute('aria-current');
        }
      });
      // On mobile, scroll the bar horizontally so the active link stays visible
      if (activeLink && this.list.scrollWidth > this.list.clientWidth) {
        const li = activeLink.parentElement;
        const left = li.offsetLeft - (this.list.clientWidth - li.offsetWidth) / 2;
        this.list.scrollTo({ left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }
    }

    /* Height of the bars that will be at the top once the page has scrolled to `y`.
       Below the main product form the theme header slides away and the sticky add to cart bar
       takes its place, so use that bar's height straight away instead of the current header. */
    topBarsAt(y) {
      const atc = document.querySelector('product-fixed-atc-bar, [data-pdn-final-bar]');
      if (atc && getComputedStyle(atc).display !== 'none') {
        const form = document.querySelector('.cc-main-product product-form');
        const formBottom = form ? form.getBoundingClientRect().bottom + window.scrollY : 0;
        if (y > formBottom) return Math.round(atc.getBoundingClientRect().height);
      }
      return this.headerOffset();
    }

    scrollToPanel(panel, behavior, pass = 0) {
      const panelTop = panel.getBoundingClientRect().top + window.scrollY;
      const bars = pass === 0 ? this.topBarsAt(panelTop - 200) : this.headerOffset();
      const offset = bars + this.extra + this.nav.offsetHeight + 16;
      const y = panelTop - offset;
      window.scrollTo({ top: Math.max(0, y), behavior: behavior || (reduceMotion.matches ? 'auto' : 'smooth') });
      if (pass === 0) panel.focus({ preventScroll: true });

      // The sticky bars can change while scrolling (the header hides, the add to cart bar and
      // this bar become fixed), so check the position again once the scroll has stopped
      if (pass >= 2) return;
      clearTimeout(this.correctTimer);
      if (this.onScrollEnd) window.removeEventListener('scrollend', this.onScrollEnd);
      const correct = () => {
        clearTimeout(this.correctTimer);
        window.removeEventListener('scrollend', this.onScrollEnd);
        setTimeout(() => {
          const wanted = this.headerOffset() + this.extra + this.nav.offsetHeight + 16;
          const delta = panel.getBoundingClientRect().top - wanted;
          const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
          if (Math.abs(delta) > 8 && !(delta > 0 && atBottom)) this.scrollToPanel(panel, 'smooth', pass + 1);
        }, 400); // let the bars finish their show / hide animation
      };
      this.onScrollEnd = correct;
      if ('onscrollend' in window) window.addEventListener('scrollend', correct, { once: true });
      this.correctTimer = setTimeout(correct, 1200);
    }

    initDescriptions() {
      this.querySelectorAll('[data-pdn-desc]').forEach((wrap) => {
        const content = wrap.querySelector('[data-pdn-desc-content]');
        const inner = wrap.querySelector('[data-pdn-desc-inner]');
        const button = wrap.querySelector('[data-pdn-desc-toggle]');
        const label = wrap.querySelector('[data-pdn-desc-label]');
        if (!content || !inner || !button) return;

        const limit = () => parseInt(mobileMq.matches ? wrap.dataset.collapsedMobile : wrap.dataset.collapsed, 10) || 300;

        const check = () => {
          if (wrap.classList.contains('is-open')) return;
          const needsToggle = inner.scrollHeight > limit() + 40;
          wrap.classList.toggle('is-collapsible', needsToggle);
          button.hidden = !needsToggle;
          content.style.maxHeight = needsToggle ? limit() + 'px' : '';
        };

        check();
        if ('ResizeObserver' in window) new ResizeObserver(check).observe(inner);
        if (mobileMq.addEventListener) mobileMq.addEventListener('change', check);

        button.addEventListener('click', () => {
          const opening = !wrap.classList.contains('is-open');
          if (opening) {
            content.style.maxHeight = inner.scrollHeight + 'px';
            wrap.classList.add('is-open');
            const done = () => {
              if (wrap.classList.contains('is-open')) content.style.maxHeight = 'none';
            };
            reduceMotion.matches ? done() : content.addEventListener('transitionend', done, { once: true });
          } else {
            content.style.maxHeight = content.scrollHeight + 'px';
            void content.offsetHeight;
            wrap.classList.remove('is-open');
            content.style.maxHeight = limit() + 'px';
            const panel = wrap.closest('[data-pdn-panel]');
            if (panel && panel.getBoundingClientRect().top < 0) this.scrollToPanel(panel);
          }
          button.setAttribute('aria-expanded', String(opening));
          label.textContent = opening ? button.dataset.less : button.dataset.more;
        });
      });
    }
  }

  customElements.define('product-detail-nav', ProductDetailNav);
})();